import type {
  FestivalQuizData,
  FestivalAnswer,
  FestivalQuestion,
  QuizAxis,
  GenreAffinity,
} from "./quiz-data";
import { FESTIVALS } from "@/lib/festival-weather/festivals";
import { upcomingFestivalListings } from "@/lib/festival-weather/listings";

export type AnswerMap = Record<string, FestivalAnswer>;

export type RankedFestival = {
  festival: FestivalQuizData;
  matchPercent: number;
  topAxis: QuizAxis;
  hasLiveJobs: boolean;
};

const GENRE_BONUS = 0.12;

export function filterByGeo(
  pool: FestivalQuizData[],
  answers: AnswerMap
): FestivalQuizData[] {
  const geoAnswer = answers["geo"];
  if (!geoAnswer?.regionFilter || geoAnswer.regionFilter === "anywhere")
    return pool;
  return pool.filter((f) => f.region === geoAnswer.regionFilter);
}

export function getUserGenreAffinity(answers: AnswerMap): GenreAffinity | null {
  const genreAnswer = answers["genre"];
  return genreAnswer?.genreAffinity ?? null;
}

export function getUserTopAxis(
  answers: AnswerMap,
  questions: FestivalQuestion[]
): QuizAxis {
  const axisWeights: Partial<Record<QuizAxis, number>> = {};
  for (const q of questions) {
    const a = answers[q.id];
    if (!a?.targets) continue;
    const multiplier = q.doubleWeight ? 2 : 1;
    for (const t of a.targets) {
      const w = (t.weight ?? 1.0) * multiplier;
      axisWeights[t.axis] = (axisWeights[t.axis] ?? 0) + w;
    }
  }
  let topAxis: QuizAxis = "crowdEnergy";
  let topWeight = 0;
  for (const [axis, weight] of Object.entries(axisWeights)) {
    if (weight > topWeight) {
      topAxis = axis as QuizAxis;
      topWeight = weight;
    }
  }
  return topAxis;
}

export function computeScore(
  festival: FestivalQuizData,
  answers: AnswerMap,
  questions: FestivalQuestion[],
  genreAffinity: GenreAffinity | null
): { raw: number; max: number } {
  let raw = 0;
  let max = 0;
  for (const q of questions) {
    const a = answers[q.id];
    if (!a?.targets) continue;
    const multiplier = q.doubleWeight ? 2 : 1;
    for (const t of a.targets) {
      const weight = t.weight ?? 1.0;
      const actual = festival.axes[t.axis];
      const similarity = 1 - Math.abs(t.ideal - actual) / 10;
      raw += similarity * weight * multiplier;
      max += weight * multiplier;
    }
  }

  const hasGenreMatch =
    genreAffinity !== null && festival.genreTags.includes(genreAffinity);
  if (genreAffinity !== null) {
    if (hasGenreMatch) raw += max * GENRE_BONUS;
    max = max * (1 + GENRE_BONUS);
  }

  return { raw, max };
}

export function rankFestivals(
  pool: FestivalQuizData[],
  answers: AnswerMap,
  questions: FestivalQuestion[],
  festivalJobSlugs: Set<string>,
  today: Date = new Date(),
): RankedFestival[] {
  const available = new Set(upcomingFestivalListings(FESTIVALS, today.toISOString().slice(0, 10)).map((listing) => listing.festival.slug));
  const filtered = filterByGeo(pool, answers).filter((festival) => available.has(festival.slug));
  const genreAffinity = getUserGenreAffinity(answers);
  const topAxis = getUserTopAxis(answers, questions);

  const scored = filtered.map((festival) => {
    const { raw, max } = computeScore(festival, answers, questions, genreAffinity);
    const pct = max > 0 ? Math.max(0, Math.round((raw / max) * 100)) : 0;
    return {
      festival,
      matchPercent: pct,
      topAxis,
      hasLiveJobs: festivalJobSlugs.has(festival.slug),
    };
  });

  scored.sort((a, b) => {
    if (Math.abs(a.matchPercent - b.matchPercent) <= 2) {
      if (a.hasLiveJobs !== b.hasLiveJobs) return a.hasLiveJobs ? -1 : 1;
      return b.matchPercent - a.matchPercent;
    }
    return b.matchPercent - a.matchPercent;
  });

  const top = scored[0]?.matchPercent ?? 0;
  let count = 3;
  if (scored.length >= 4 && top - scored[3].matchPercent <= 15) count = 4;
  if (scored.length >= 5 && top - scored[4].matchPercent <= 15) count = 5;

  return scored.slice(0, count);
}
